/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState } from "react";
import PropTypes from "prop-types";

/**
 * ImageComponent
 * ----------------
 * - Renders an image from URL
 * - Fallback placeholder when missing/broken
 * - On click → opens a fullscreen preview modal (lightbox)
 */

function ImageComponent({
    src,
    alt,
    width,
    height,
    shape,
    fit,
    className,
}) {
    const [hasError, setHasError] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);

    const resolvedWidth = typeof width === "number" ? `${width}px` : width;
    const resolvedHeight = typeof height === "number" ? `${height}px` : height;

    const borderRadius =
        shape === "circle" ? "50%" : shape === "rounded" ? "0.75rem" : "0";

    //FALLBACK IF IMAGE MISSING  
    if (!src || hasError) {
        return (
            <div
                className={className}
                style={{
                    width: resolvedWidth,
                    height: resolvedHeight,
                    borderRadius,
                    backgroundColor: "#E5E7EB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.75rem",
                    color: "#6B7280",
                    overflow: "hidden",
                    border: "1px solid #D1D5DB",
                }}
                title={alt}
            >
                No image
            </div>
        );
    }

    return (
        <>
            {/* Thumbnail */}
            <div
                className={className}
                style={{
                    width: resolvedWidth,
                    height: resolvedHeight,
                    borderRadius,
                    overflow: "hidden",
                    display: "inline-flex",
                    border: "1px solid #E5E7EB",
                    cursor: "pointer",
                }}
                onClick={() => setPreviewOpen(true)}
                title={alt}
            >
                <img
                    src={src}
                    alt={alt}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: fit,
                        display: "block",
                    }}
                    onError={() => setHasError(true)}
                />
            </div>

            {/* Fullscreen Preview Modal */}
            {previewOpen && (
                <div
                    onClick={() => setPreviewOpen(false)}
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.85)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                        cursor: "zoom-out",
                        padding: "10px", // prevents mobile edges cut-off
                    }}
                >
                    <img
                        src={src}
                        alt={alt}
                        style={{
                            width: "100%",
                            height: "100%",
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            borderRadius: "0.75rem",
                        }}
                    />
                </div>
            )}
        </>
    );
}

ImageComponent.defaultProps = {
    src: null,
    alt: "image",
    width: 48,
    height: 48,
    shape: "rounded",
    fit: "cover",
    className: "",
};

ImageComponent.propTypes = {
    src: PropTypes.string,
    alt: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    shape: PropTypes.oneOf(["square", "rounded", "circle"]),
    fit: PropTypes.oneOf(["cover", "contain", "fill", "none", "scale-down"]),
    className: PropTypes.string,
};

export default ImageComponent;
